import React from "react";
import { 
  Activity, 
  Bell, 
  Settings, 
  Volume2, 
  VolumeX, 
  TrendingUp, 
  TrendingDown, 
  Layers,
  Database,
  Code2,
  User,
  LogIn,
  Crown,
  Shield,
  Sparkles
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import MinoLogoIcon from "./MinoLogoIcon";

export default function Header({
  symbols,
  currentSymbol,
  onSelectSymbol,
  timeframes,
  currentTimeframe,
  onSelectTimeframe,
  candleCount,
  onSelectCandleCount,
  tick,
  status,
  activeAlertsCount,
  onOpenAlerts,
  onOpenSettings,
  onOpenPineEditor,
  soundEnabled,
  onToggleSound,
}) {
  const { user, tier, tierInfo, isAuthenticated, isAdmin, isPro, openAuth, setUserProfileModalOpen, navigateTo } = useAuth();
  return (
    <header className="bg-slate-900/95 backdrop-blur border-b border-slate-800 sticky top-0 z-40">
      {/* Top Bar: Brand, Symbol Selector, Actions */}
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 sm:py-2.5">
        {/* Left: Brand & Symbol */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="MINO PRO Logo" 
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-contain shadow-md shadow-amber-500/20" 
            />
            <div>
              <div className="flex items-center gap-1">
                <span className="font-extrabold text-xs sm:text-sm tracking-wide bg-gradient-to-r from-amber-400 to-yellow-200 bg-clip-text text-transparent whitespace-nowrap">
                  MINO PRO
                </span>
                <span className="hidden xs:inline-block text-[9px] uppercase tracking-wider px-1 py-0.2 rounded bg-amber-500/10 text-amber-400 font-bold border border-amber-500/20">
                  AI
                </span>
              </div>
            </div>
          </div>

          <div className="h-5 w-px bg-slate-800" />

          {/* Symbol Select */}
          <select
            value={currentSymbol}
            onChange={(e) => onSelectSymbol(e.target.value)}
            className="bg-slate-950 border border-slate-700 text-slate-100 font-bold text-xs sm:text-sm rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 focus:outline-none focus:border-amber-400 transition hover:border-slate-600 cursor-pointer max-w-[135px] sm:max-w-[200px] truncate"
          >
            {symbols.map((sym) => (
              <option key={sym.symbol} value={sym.symbol} className="bg-slate-900 text-slate-100">
                {sym.symbol} - {sym.name}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop Middle: Timeframe Selector & Depth (Hidden on mobile < md, shown on md+) */}
        <div className="hidden md:flex items-center gap-2">
          <div className="flex items-center bg-slate-950/80 p-0.5 rounded-lg border border-slate-800 text-xs font-semibold">
            {timeframes.map((tf) => (
              <button
                key={tf}
                onClick={() => onSelectTimeframe(tf)}
                className={`px-2.5 py-1 rounded transition-all cursor-pointer ${
                  currentTimeframe === tf
                    ? "bg-amber-500 text-slate-950 font-bold shadow-sm shadow-amber-500/30"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                }`}
              >
                {tf}
              </button>
            ))}
          </div>

          {/* Candle Depth Selector */}
          <div className="hidden lg:flex items-center gap-1.5 bg-slate-950/80 px-2.5 py-1 rounded-lg border border-slate-800 text-xs">
            <Database className="w-3.5 h-3.5 text-amber-400" />
            <select
              value={candleCount !== undefined && candleCount !== null ? candleCount : 10000}
              onChange={(e) => onSelectCandleCount(parseInt(e.target.value))}
              className="bg-transparent text-slate-300 font-mono text-[11px] focus:outline-none cursor-pointer"
              title="Độ sâu lịch sử nến"
            >
              <option value="5000" className="bg-slate-900">5k nến</option>
              <option value="10000" className="bg-slate-900">10k nến</option>
              <option value="20000" className="bg-slate-900">{isPro ? "20k nến" : "🔒 20k nến (PRO)"}</option>
              <option value="50000" className="bg-slate-900">{isPro ? "50k nến" : "🔒 50k nến (PRO)"}</option>
              {isAdmin && (
                <>
                  <option value="100000" className="bg-slate-900 text-amber-400 font-semibold">100k nến (Admin)</option>
                  <option value="0" className="bg-slate-900 text-amber-300 font-bold">👑 Toàn bộ nến (ALL)</option>
                </>
              )}
            </select>
          </div>

          {/* Desktop Tick Box */}
          {tick && (
            <div className="hidden xl:flex items-center gap-2.5 px-2.5 py-1 bg-slate-950/80 rounded-lg border border-slate-800/80 text-xs font-mono">
              <div>
                <span className="text-slate-400 text-[9px] block leading-none">BID</span>
                <span className="text-emerald-400 font-bold text-[11px]">{tick.bid}</span>
              </div>
              <div className="h-3.5 w-px bg-slate-800" />
              <div>
                <span className="text-slate-400 text-[9px] block leading-none">ASK</span>
                <span className="text-rose-400 font-bold text-[11px]">{tick.ask}</span>
              </div>
              <div className="h-3.5 w-px bg-slate-800" />
              <div>
                <span className="text-slate-400 text-[9px] block leading-none">SPREAD</span>
                <span className="text-amber-400 font-bold text-[11px]">{tick.spread}</span>
              </div>
            </div>
          )}
        </div>

        {/* Right: Sound, Pine Editor, Alert, Profile Buttons */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Sound Toggle */}
          <button
            onClick={onToggleSound}
            title={soundEnabled ? "Tắt âm thanh chuông báo" : "Bật âm thanh chuông báo"}
            className={`p-1.5 sm:p-2 rounded-lg border transition cursor-pointer ${
              soundEnabled
                ? "bg-slate-800 border-slate-700 text-amber-400 hover:bg-slate-700"
                : "bg-slate-900 border-slate-800 text-slate-500 hover:bg-slate-800"
            }`}
          >
            {soundEnabled ? <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
          </button>

          {/* Pine Script Editor Button */}
          <button
            onClick={onOpenPineEditor}
            className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-gradient-to-r from-cyan-900/50 to-blue-900/50 hover:from-cyan-800/70 hover:to-blue-800/70 text-cyan-300 border border-cyan-500/40 rounded-lg text-xs font-bold transition shadow-sm cursor-pointer"
            title="Mở trình soạn thảo Pine Script v5"
          >
            <Code2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400" />
            <span className="hidden sm:inline">Pine Editor</span>
          </button>

          {/* Admin Button (Only if Super Admin) */}
          {isAdmin && (
            <button
              onClick={() => navigateTo('admin')}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-purple-950/60 hover:bg-purple-900/70 text-purple-300 border border-purple-500/50 rounded-lg text-xs font-bold transition shadow-sm cursor-pointer animate-pulse"
              title="Trang Quản Trị Hệ Thống"
            >
              <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-400" />
              <span className="hidden md:inline">Quản Trị</span>
            </button>
          )}

          {/* User Profile / Login Button */}
          {isAuthenticated ? (
            <button
              onClick={() => setUserProfileModalOpen(true)}
              className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2.5 py-1 bg-slate-950/80 hover:bg-slate-900 border border-slate-700/80 rounded-xl transition cursor-pointer group"
              title="Quản lý tài khoản cá nhân & Cấp bậc"
            >
              <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center font-black text-xs border ${
                tier === 'ADMIN'
                  ? 'bg-purple-950 text-purple-300 border-purple-500/50'
                  : tier === 'PRO'
                  ? 'bg-amber-950 text-amber-300 border-amber-500/50'
                  : 'bg-cyan-950 text-cyan-300 border-cyan-500/50'
              }`}>
                {user.username.slice(0, 1).toUpperCase()}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-xs font-bold text-slate-200 group-hover:text-cyan-400 transition leading-tight">
                  {user.username}
                </div>
                <div className="text-[10px] font-extrabold flex items-center gap-1 leading-none">
                  <span className={tierInfo.color.split(' ')[0]}>{tierInfo.badge}</span>
                </div>
              </div>
            </button>
          ) : (
            <button
              onClick={() => openAuth('login')}
              className="flex items-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 rounded-lg text-xs font-bold transition shadow-md shadow-cyan-500/20 cursor-pointer"
            >
              <LogIn className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="text-xs">Đăng Nhập</span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Sub Bar: Timeframes & Live Tick (Visible on mobile < md) */}
      <div className="md:hidden flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-950/90 border-t border-slate-800/80 overflow-x-auto scrollbar-none">
        {/* Timeframe Pills */}
        <div className="flex items-center gap-1 shrink-0 bg-slate-900/90 p-0.5 rounded-lg border border-slate-800 text-[11px] font-semibold">
          {timeframes.map((tf) => (
            <button
              key={tf}
              onClick={() => onSelectTimeframe(tf)}
              className={`px-2 py-0.5 rounded transition-all cursor-pointer ${
                currentTimeframe === tf
                  ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>

        {/* Live Tick on Mobile */}
        {tick ? (
          <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-900/90 rounded border border-slate-800 text-[10px] font-mono shrink-0">
            <span className="text-emerald-400 font-bold">{tick.bid}</span>
            <span className="text-slate-600">/</span>
            <span className="text-rose-400 font-bold">{tick.ask}</span>
            <span className="text-slate-600">|</span>
            <span className="text-amber-400 font-bold">{tick.spread}p</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span>LIVE</span>
          </div>
        )}
      </div>
    </header>
  );
}